import merge from "lodash.merge";
import { ref, Ref } from "vue";

export type EntityMapState<V> = Record<string, V>;
export type UpdateEntity<V> = (list: V[]) => V[];
type KeyGetter<V> = (item: V) => string;

export const updateEntityHelper = (
  data: Entity | Entity[],
  map: Record<number, Entity>
) => {
  const list = Array.isArray(data) ? data : [data];
  list.forEach((item) => {
    const target = map[item.id!];
    if (target) {
      merge(target, item);
    } else {
      map[item.id!] = item;
    }
  });
};

export interface EntityStore<V> {
  keyFieldOrGetter: keyof V | KeyGetter<V>;
  map: Ref<EntityMapState<V>>;
  updateMap: UpdateEntity<V>;
  getEntity: (id: string | number) => V | undefined;
  deleteItem: (id: string | number) => V | null;
  getKey: KeyGetter<V>;
}

export const entityStoreFactory = <V extends Entity>(
  keyFieldOrGetter: keyof V | KeyGetter<V>
): EntityStore<V> => {
  const map = ref<EntityMapState<V>>({});
  const getKey: KeyGetter<V> =
    typeof keyFieldOrGetter === "function"
      ? keyFieldOrGetter
      : (item: V) => item[keyFieldOrGetter] as string;
  const updateMap: UpdateEntity<V> = (list: V[]) => {
    return list.map((item) => {
      const key = getKey(item);
      const target = map.value[key as string];
      if (target) {
        Object.assign(target, item);
      } else {
        map.value[key as string] = item;
      }
      return target ?? item;
    });
  };
  const deleteItem = (id: string | number) => {
    const target = map.value[id as string | number];
    if (target) {
      delete map.value[id as string | number];
      return target;
    }
    return null;
  };
  const getEntity = (id: string | number): V | undefined => {
    return map.value[id];
  };
  return {
    map,
    updateMap,
    getEntity,
    deleteItem,
    keyFieldOrGetter: keyFieldOrGetter,
    getKey,
  };
};
